// src/components/files/FileList.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import FileItem from './FileItem';
import FilePreview from './FilePreview';
import { FileDocument } from '../../types/files';

const FileList: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToFolder, selectItem, isSelected, clearSelection } = useFileManager();
  const { contents, sortBy, sortOrder, selectedItems } = state;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileDocument | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!contents) return null;

  // Sort items
  const sortedFolders = [...contents.folders].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'date':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      default:
        comparison = a.name.localeCompare(b.name);
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const sortedDocuments = [...contents.documents].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'date':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      case 'size':
        comparison = a.file_size - b.file_size;
        break;
      default:
        comparison = a.title.localeCompare(b.title);
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const handleFolderDoubleClick = (folderId: number) => {
    navigateToFolder(folderId);
  };

  const handleDocumentDoubleClick = (doc: FileDocument) => {
    const index = sortedDocuments.findIndex((d) => d.id === doc.id);
    setPreviewFile(doc);
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handlePreviewNavigate = (index: number) => {
    if (index >= 0 && index < sortedDocuments.length) {
      setPreviewFile(sortedDocuments[index]);
      setPreviewIndex(index);
    }
  };

  const handleItemClick = (
    e: React.MouseEvent,
    id: number,
    type: 'folder' | 'document'
  ) => {
    selectItem({ id, type }, e.ctrlKey || e.metaKey, e.shiftKey);
  };

  const allItems = [
    ...sortedFolders.map(f => ({ ...f, itemType: 'folder' as const })),
    ...sortedDocuments.map(d => ({ ...d, itemType: 'document' as const, name: d.title })),
  ];

  const allSelected = allItems.length > 0 && selectedItems.length === allItems.length;

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      // TODO: Implement select all in context
      clearSelection();
    }
  };

  return (
    <Box>
      {/* Headers */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'grey.50',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Box sx={{ width: 40 }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={selectedItems.length > 0 && !allSelected}
            onChange={handleSelectAll}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{ flex: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}
        >
          {t('fileManager.list.name', 'Nom')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: 80, textAlign: 'right', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}
        >
          {t('fileManager.list.size', 'Taille')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: 120, textAlign: 'right', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mr: 6 }}
        >
          {t('fileManager.list.modified', 'Modifie')}
        </Typography>
      </Box>

      {/* Items */}
      <Box>
        {/* Folders */}
        {sortedFolders.map((folder) => (
          <FileItem
            key={`folder-${folder.id}`}
            type="folder"
            id={folder.id}
            name={folder.name}
            color={folder.color}
            icon={folder.icon}
            isFavorite={folder.is_favorite}
            createdAt={folder.created_at}
            isSelected={isSelected(folder.id, 'folder')}
            onClick={(e) => handleItemClick(e, folder.id, 'folder')}
            onDoubleClick={() => handleFolderDoubleClick(folder.id)}
            viewMode="list"
          />
        ))}

        {/* Documents */}
        {sortedDocuments.map((doc) => (
          <FileItem
            key={`doc-${doc.id}`}
            type="document"
            id={doc.id}
            name={doc.title}
            mimeType={doc.file_type}
            fileSize={doc.file_size}
            fileUrl={doc.file_url}
            createdAt={doc.created_at}
            isFavorite={doc.is_favorite}
            isSelected={isSelected(doc.id, 'document')}
            onClick={(e) => handleItemClick(e, doc.id, 'document')}
            onDoubleClick={() => handleDocumentDoubleClick(doc)}
            viewMode="list"
          />
        ))}
      </Box>

      {/* File Preview Dialog */}
      <FilePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        file={previewFile}
        files={sortedDocuments}
        currentIndex={previewIndex}
        onNavigate={handlePreviewNavigate}
      />
    </Box>
  );
};

export default FileList;
